// « CONFIGURATION » — the org's setup gaps, read once for the whole console
// and held across pages (the header outlives them), like usePipelineBadges.
// Every line is derived server-side (Organizations::SetupCheckService); this
// only holds the latest reading.
//
// ★ FRESH AFTER A FIX: the tab's own writes to anything a line reads trigger
// ONE coalesced re-read (lib/apiWrites), so saving a tax number on the Taxes
// page drops the header's count without a navigation. Other people's writes (a
// technician's phone registering) are caught when the dialog opens — it reads
// again then.
//
// A reading, never a gate: a failed FIRST read leaves the button hidden; a
// failed re-read keeps the last good reading (a network blip after a save must
// not make the button vanish).
import { shallowRef } from 'vue'
import { jsonapiClient } from '@/api/client'
import { onApiWrite } from '@/lib/apiWrites'
import type { SetupCheckMeta } from '@/lib/setupCheck'

const meta = shallowRef<SetupCheckMeta | null>(null)
let forOrg: string | null = null
let seq = 0

const refetch = async () => {
  if (!forOrg) return
  const mine = ++seq
  try {
    const response = await jsonapiClient.get('/api/setup_check')
    if (mine === seq) meta.value = response.data.meta
  } catch {
    // Keep what we had: `ensure` already cleared it for a new org.
  }
}

// The resources a line reads (see the service's checks). A burst of writes — a
// crew page saving several fields — is ONE re-read, 500 ms after the last.
const COUNTED = new Set([
  'organizations', 'organization_taxes', 'depots', 'job_templates', 'work_periods', 'work_period_runs',
  'memberships', 'squads', 'contacts', 'companies', 'service_locations', 'imports', 'jobs',
])
let pending: ReturnType<typeof setTimeout> | null = null
onApiWrite(resource => {
  if (!forOrg || !COUNTED.has(resource)) return
  if (pending) clearTimeout(pending)
  pending = setTimeout(() => { pending = null; void refetch() }, 500)
})

export function useSetupCheck() {
  // The org switched (or the first read): forget the other shop's lines.
  const ensure = (orgId: string) => {
    if (forOrg === orgId) return
    forOrg = orgId
    meta.value = null
    void refetch()
  }

  return { meta, refetch, ensure }
}
